import React, { useEffect } from "react";
import { Row, Col, Button, Input, Drawer, DatePicker, Select, Form, message } from "antd";
import TextArea from "antd/lib/input/TextArea";
import Close from "../../../../assets/images/close-x.svg";
import CancelArrow from "../../../../assets/images/cancelArrow.svg";
import useTransactionStore from "../../../../stores/useTransactionStore";

const keyBtn = {
  width: "100%",
  height: "8vh",
  borderRadius: 4,
  background: "#92909880",
  border: "none",
  fontSize: "1.15em",
  fontWeight: 500,
  color: "#0f0718",
  cursor: "pointer",
};

const ManualDiscountDrawer = ({
  manualDiscountModalVisible,
  setManualDiscountModalVisible,
  manualDiscountForm,
  manualDiscountInput,
  setManualDiscountInput,
  manualDiscountTypes,
  handleManualDiscountKeyPress,
  applyManualDiscount,
  selectedManualDiscountType,
  setSelectedManualDiscountTypeValue,
  enableManualDiscountInput,
  removeAllDiscounts,
  selectedProductInCart,
  setSelectedProductInCart,
  cart,
  tillData,
  totalBillDiscountData,
  discountAdditionalInfo,
  setDiscountAdditionalInfo,
  discountAdditionalInfoForm,
  discountInforApplication,
  handleDiscountTypeChange,
  valueInputRef,
  searchInputRef,
}) => {
  const currSymbol = tillData?.tillAccess?.csBunit?.currencies?.[0]?.currSymbol || "";
  const precision = tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision || 2;
  const hasAdditionalInfo = discountAdditionalInfo?.length > 0;
  const hasProduct = selectedProductInCart && Object.keys(selectedProductInCart).length > 0;
  const isLineLevel = selectedManualDiscountType && (selectedManualDiscountType.type === "FD" || selectedManualDiscountType.type === "PD");
  const cartItems = (cart?.items || []).filter((i) => i.isCanceled !== "Y");

  // When the drawer (re)opens, check if the selected cart item (or the cart
  // itself for total-level discounts) already has a manual discount applied.
  // If so, prepopulate the form with the existing discount type and value so
  // the cashier sees what was previously applied instead of a blank slate.
  // Only reset to blank when no discount exists. (BUG-1098)
  useEffect(() => {
    if (manualDiscountModalVisible) {
      // Check for existing line-level discount on the selected product
      const existingLineDiscount = hasProduct && selectedProductInCart?.mPricingruleId
        ? manualDiscountTypes.find((rule) => rule.mPricingrulesId === selectedProductInCart.mPricingruleId)
        : null;

      // Check for existing total-level discount on the cart
      const existingTotalDiscount = !existingLineDiscount && cart?.manualDiscountData?.totalLevelDiscount?.length > 0
        ? (() => {
            const lastTotalDiscount = cart.manualDiscountData.totalLevelDiscount[cart.manualDiscountData.totalLevelDiscount.length - 1];
            const matchedRule = manualDiscountTypes.find((rule) => rule.mPricingrulesId === lastTotalDiscount?.pricingRule);
            return matchedRule ? { rule: matchedRule, discountValue: lastTotalDiscount.discountValue } : null;
          })()
        : null;

      // Check for existing line-level discount via manualDiscountData (when product has no mPricingruleId but data exists)
      const existingLineFromData = !existingLineDiscount && !existingTotalDiscount && hasProduct
        ? (() => {
            const lineDiscounts = cart?.manualDiscountData?.lineLevelDiscount || [];
            const match = lineDiscounts.find((d) => d.lineId === selectedProductInCart.lineId);
            if (!match) return null;
            const matchedRule = manualDiscountTypes.find((rule) => rule.mPricingrulesId === match.pricingRule);
            return matchedRule ? { rule: matchedRule, discountValue: match.discountValue } : null;
          })()
        : null;

      if (existingLineDiscount) {
        // Prepopulate with the line-level discount applied to the selected item
        const lineDiscountData = (cart?.manualDiscountData?.lineLevelDiscount || []).find(
          (d) => d.lineId === selectedProductInCart.lineId
        );
        const discountValue = lineDiscountData?.discountValue ?? (
          existingLineDiscount.discountType === "V"
            ? (existingLineDiscount.amountDiscount ?? "")
            : (existingLineDiscount.percentageDiscount ?? "")
        );
        setSelectedManualDiscountTypeValue(existingLineDiscount);
        setManualDiscountInput(String(discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingLineDiscount.mPricingrulesId,
          discountValue: String(discountValue || ""),
        });
      } else if (existingTotalDiscount) {
        // Prepopulate with the total-level discount
        setSelectedManualDiscountTypeValue(existingTotalDiscount.rule);
        setManualDiscountInput(String(existingTotalDiscount.discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingTotalDiscount.rule.mPricingrulesId,
          discountValue: String(existingTotalDiscount.discountValue || ""),
        });
      } else if (existingLineFromData) {
        // Prepopulate from manualDiscountData line-level entry
        setSelectedManualDiscountTypeValue(existingLineFromData.rule);
        setManualDiscountInput(String(existingLineFromData.discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingLineFromData.rule.mPricingrulesId,
          discountValue: String(existingLineFromData.discountValue || ""),
        });
      } else {
        // No existing discount — reset to blank state
        manualDiscountForm.resetFields();
        setManualDiscountInput("");
        setSelectedManualDiscountTypeValue(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDiscountModalVisible, manualDiscountTypes]);

  const handleClose = () => {
    manualDiscountForm.resetFields();
    discountAdditionalInfoForm.resetFields();
    setManualDiscountInput("");
    setSelectedManualDiscountTypeValue(null);
    setManualDiscountModalVisible(false);
    setDiscountAdditionalInfo([]);
    useTransactionStore.getState().setDiscountAppliedData(null);
    searchInputRef?.current?.focus();
  };

  const handleSelectType = (rule) => {
    handleDiscountTypeChange(rule.mPricingrulesId);
    manualDiscountForm.setFieldsValue({ discountName: rule.mPricingrulesId });
  };

  const handleSelectCartItem = (item) => {
    if (setSelectedProductInCart) {
      setSelectedProductInCart(item);
    }
  };

  const press = (key) => (e) => {
    e.preventDefault();
    handleManualDiscountKeyPress(key);
  };

  const getDiscountPreview = () => {
    if (!selectedManualDiscountType || !manualDiscountInput || Number(manualDiscountInput) <= 0) return null;
    if (selectedManualDiscountType.type === "TD") {
      if (!totalBillDiscountData) return null;
      return {
        discount: totalBillDiscountData.discountedAmount?.toFixed(precision),
        afterDiscount: totalBillDiscountData.finalAmount?.toFixed(precision),
      };
    }
    if (hasProduct) {
      const discountAmt =
        selectedManualDiscountType.discountType === "P"
          ? selectedProductInCart.realPrice * selectedProductInCart.weight * (Number(manualDiscountInput) / 100)
          : Number(manualDiscountInput);
      return {
        discount: discountAmt.toFixed(precision),
        afterDiscount: (selectedProductInCart.nettotal - discountAmt).toFixed(precision),
      };
    }
    return null;
  };

  const preview = getDiscountPreview();
  const needsProductSelection = isLineLevel && !hasProduct;

  return (
    <Drawer
      placement="bottom"
      closable={false}
title={
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      width: "100%",
      padding: "0.3vh 0",
    }}
  >
    <p
      style={{
        fontSize: "1.3em",
        fontWeight: "500",
        color: "#0F0718",
        margin: 0,
        textAlign: "center",
      }}
    >
      Manual Discount
    </p>

    <img
      src={Close}
      onClick={handleClose}
      style={{
        cursor: "pointer",
        width: "1.2em",
        position: "absolute",
        right: 0,
      }}
      alt="close"
    />
  </div>
}
      open={manualDiscountModalVisible}
      className="filter"
      styles={{ 
        header: {
  borderBottom: "none",
},
        body: {background:"#f3f4f9", padding: "1em 1.5em", overflow: "hidden" } }}
      onClose={handleClose}
      height="73vh"
    >
      <Form form={manualDiscountForm} onFinish={applyManualDiscount}>
        <Form.Item name="discountName" hidden>
          <Input />
        </Form.Item>

        <Row gutter={[24, 0]}>
          {/* ── LEFT: Compact Keypad ── */}
          <Col span={hasAdditionalInfo ? 9 : 10}>
            {/* Selected type + product context */}

<div
  style={{
    marginBottom: "0.8vh",
    textAlign: "center",
    minHeight: "3.5vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
{selectedManualDiscountType ? (
  <>
    <span
      style={{
        fontWeight: 500,
        color: "#0F0718",
        fontSize: "0.95em",
      }}
    >
      {selectedManualDiscountType.name}
    </span>

    <span
      style={{
        fontSize: "0.75em",
        padding: "2px 8px",
        borderRadius: 10,
        background: "rgba(47,56,86,0.08)",
        color: "#2F3856",
        fontWeight: 500,
        marginLeft: 8,
      }}
    >
      {selectedManualDiscountType.discountType === "V"
        ? "Amount"
        : "Percentage"}
    </span>
  </>
) : (
  <span style={{ visibility: "hidden" }}>placeholder</span>
)}
              </div>


            {/* Show selected product only for line-level discounts */}
            {isLineLevel && hasProduct && (
              <div
                style={{
                  padding: "0.5vh 0.8em",
                  marginBottom: "0.6vh",
                  background: "rgba(82,196,26,0.06)",
                  borderRadius: 4,
                  border: "1px solid rgba(82,196,26,0.2)",
                  fontSize: "0.85em",
                }}
              >
                <span style={{ color: "#929098" }}>Item: </span>
                <span style={{ fontWeight: 500, color: "#0F0718" }}>{selectedProductInCart.name}</span>
                <span style={{ color: "#929098" }}>
                  {" "}
                  &mdash; {currSymbol}
                  {selectedProductInCart.nettotal}
                </span>
              </div>
            )}

            {/* Product picker for line-level with no product selected */}
            {needsProductSelection && (
              <div
                style={{
                  padding: "0.5vh 0.8em",
                  marginBottom: "0.6vh",
                  background: "rgba(207,19,34,0.04)",
                  borderRadius: 4,
                  border: "1px solid rgba(207,19,34,0.15)",
                  fontSize: "0.82em",
                  color: "#cf1322",
                }}
              >
                Select a product from the list on the right to apply line-level discount
              </div>
            )}

            <Form.Item name="discountValue" style={{ marginBottom: "0.8vh" }}>
              <Input
                disabled={!selectedManualDiscountType || enableManualDiscountInput}
                ref={valueInputRef}
                value={manualDiscountInput}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*\.?\d*$/.test(val)) {
                    setManualDiscountInput(val);
                    manualDiscountForm.setFieldsValue({ discountValue: val });
                  }
                }}
                onPressEnter={(e) => {
                  e.preventDefault();
                  manualDiscountForm.submit();
                }}
                style={{ borderRadius: 4, height: "8vh", fontSize: "1.3em", textAlign: "right", paddingRight: "0.8em" }}
                suffix={
                  <span style={{ fontSize: "1.1em", fontWeight: 600, color: "#2F3856" }}>
                    {selectedManualDiscountType?.discountType === "V" ? currSymbol : selectedManualDiscountType ? "%" : ""}
                  </span>
                }
                placeholder={
                  !selectedManualDiscountType
                    ? "Select a discount type"
                    : selectedManualDiscountType.discountType === "V"
                      ? "Enter Amount"
                      : "Enter %"
                }
              />
            </Form.Item>

            <Row gutter={[2, 2]}>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("1")}>1</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("2")}>2</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("3")}>3</button></Col>
              <Col span={6}>
<button
  type="button"
  style={{
    ...keyBtn,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
  onClick={press("x")}
>
  <img
    src={CancelArrow}
    alt="backspace"
    width={18}
    height={11}
    style={{
      display: "block",
      objectFit: "contain",
    }}
  />
</button>
              </Col>

              <Col span={6}><button type="button" style={keyBtn} onClick={press("4")}>4</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("5")}>5</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("6")}>6</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("clear")}>Clear</button></Col>

              <Col span={6}><button type="button" style={keyBtn} onClick={press("7")}>7</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("8")}>8</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("9")}>9</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press("0")}>0</button></Col>

              <Col span={6}><button type="button" style={keyBtn} onClick={press("00")}>00</button></Col>
              <Col span={6}><button type="button" style={keyBtn} onClick={press(".")}>.</button></Col>
              <Col span={12}>
                <button
                  type="button"
                  style={{
                    ...keyBtn,
                    background: selectedManualDiscountType && (!isLineLevel || hasProduct) ? "#2F3856" : "#92909880",
                    color: selectedManualDiscountType && (!isLineLevel || hasProduct) ? "#fff" : "#0f0718",
                    fontWeight: 600,
                    opacity: selectedManualDiscountType && (!isLineLevel || hasProduct) ? 1 : 0.5,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (needsProductSelection) {
                      message.warning("Please select a product from cart first");
                      return;
                    }
                    manualDiscountForm.submit();
                  }}
                  disabled={!selectedManualDiscountType}
                >
                  Apply Discount
                </button>
              </Col>
            </Row>

            {preview && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "0.8vh",
                  padding: "0.6vh 0.8em",
                  background: "rgba(47,56,86,0.03)",
                  borderRadius: 4,
                  border: "1px solid rgba(146,144,152,0.12)",
                  fontSize: "0.9em",
                }}
              >
                <span>
                  <span style={{ color: "#929098" }}>Discount: </span>
                  <span style={{ fontWeight: 500, color: "#cf1322" }}>
                    -{currSymbol}
                    {preview.discount}
                  </span>
                </span>
                <span>
                  <span style={{ color: "#929098" }}>After: </span>
                  <span style={{ fontWeight: 600, color: "#0F0718" }}>
                    {currSymbol}
                    {preview.afterDiscount}
                  </span>
                </span>
              </div>
            )}
          </Col>

          {/* ── RIGHT: Discount Types + Cart Items ── */}
          <Col span={hasAdditionalInfo ? 8 : 14}>
            {/* Cart item picker — shown for line-level discounts */}
            {isLineLevel && cartItems.length > 0 && (
              <div style={{ marginBottom: "0.8vh" }}>
                <p style={{ fontWeight: 500, color: "#0F0718", margin: "0 0 0.4em", fontSize: "0.85em" }}>
                  {hasProduct ? "Selected Item" : "Select Item from Cart"}
                </p>
                <div style={{ overflowY: "auto", maxHeight: hasProduct ? "auto" : "10vh" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {(hasProduct ? [selectedProductInCart] : cartItems).map((item, idx) => {
                      const isActive = hasProduct && selectedProductInCart?.key === item.key;
                      return (
                        <div
                          key={item.key ?? idx}
                          onClick={() => !hasProduct && handleSelectCartItem(item)}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.4em 0.8em",
                            borderRadius: 4,
                            border: isActive ? "1.5px solid #2F3856" : "1px solid rgba(146,144,152,0.15)",
                            background: isActive ? "rgba(47,56,86,0.04)" : "#fff",
                            cursor: hasProduct ? "default" : "pointer",
                            fontSize: "0.85em",
                          }}
                        >
                          <span style={{ fontWeight: 500, color: "#0F0718", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.name}
                          </span>
                          <span style={{ color: "#929098", marginLeft: 8, flexShrink: 0 }}>
                            {currSymbol}
                            {item.nettotal}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Discount type list */}
            <div style={{ overflowY: "auto",maxHeight: isLineLevel ? (hasProduct ? "40vh" : "34vh") : "42vh", paddingRight: 4 }}>
              {manualDiscountTypes.length === 0 ? (
                <p style={{ textAlign: "center", color: "#929098", padding: "2em 0" }}>No discount types available</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {manualDiscountTypes.map((rule) => {
                    const isSelected = selectedManualDiscountType?.mPricingrulesId === rule.mPricingrulesId;
                    return (
                      <div
                        key={rule.mPricingrulesId}
                        onClick={() => handleSelectType(rule)}
                        style={{
                          padding: "0.8em 1em",
                          borderRadius: 6,
                          cursor: "pointer",
                          border: isSelected ? "2px solid #2F3856" : "1.5px solid rgba(146,144,152,0.2)",
                          background: isSelected ? "rgba(47,56,86,0.04)" : "#fff",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500, color: isSelected ? "#2F3856" : "#0F0718", fontSize: "0.92em" }}>{rule.name}</span>
                          {isSelected && <span style={{ color: "#2F3856", fontSize: "1em", fontWeight: "bold" }}>&#10003;</span>}
                        </div>
                        <div style={{ display: "flex", gap: 5, marginTop: "0.3em" }}>
                          <span
                            style={{
                              fontSize: "0.72em",
                              padding: "1px 7px",
                              borderRadius: 10,
                              background: rule.type === "TD" ? "rgba(47,56,86,0.08)" : "rgba(82,196,26,0.08)",
                              color: rule.type === "TD" ? "#2F3856" : "#389e0d",
                              fontWeight: 500,
                            }}
                          >
                            {rule.type === "TD" ? "Total Bill" : rule.type === "FD" || rule.type === "PD" ? "Line Item" : rule.type || ""}
                          </span>
                          <span
                            style={{
                              fontSize: "0.72em",
                              padding: "1px 7px",
                              borderRadius: 10,
                              background: "rgba(146,144,152,0.1)",
                              color: "#929098",
                            }}
                          >
                            {rule.discountType === "V"
                              ? rule.amountDiscount
                                ? `${currSymbol}${rule.amountDiscount} Fixed`
                                : `${currSymbol} Amount`
                              : rule.percentageDiscount
                                ? `${rule.percentageDiscount}% Fixed`
                                : "Percentage"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              className="discount-remove-btn"
              style={{ width: "100%", marginTop: "0.8vh", height: "5vh", fontSize: "1em" }}
              onClick={() => {
                manualDiscountForm.resetFields();
                setManualDiscountInput("");
                setSelectedManualDiscountTypeValue(null);
                removeAllDiscounts(true);
              }}
            >
              Remove All Discounts
            </Button>
          </Col>

          {/* ── ADDITIONAL INFO ── */}
          {hasAdditionalInfo && (
            <Col span={7}>
              <p style={{ fontWeight: 500, color: "#0F0718", margin: "0 0 0.4em", fontSize: "0.9em" }}>Additional Information</p>
              <div
                style={{
                  overflowY: "auto",
                  maxHeight: "30vh",
                  padding: "0.6em",
                  background: "rgba(47,56,86,0.02)",
                  borderRadius: 6,
                  border: "1px solid rgba(146,144,152,0.12)",
                }}
              >
                <Form form={discountAdditionalInfoForm} layout="vertical" size="small">
                  {discountAdditionalInfo.map((field) => (
                    <Form.Item
                      key={field.fieldName}
                      name={field.fieldName}
                      label={
                        <span style={{ fontWeight: 500, color: "#0F0718", fontSize: "0.82em" }}>
                          {field.mandatory === "Y" && <span style={{ color: "#cf1322", marginRight: 3 }}>*</span>}
                          {field.displayLabel}
                        </span>
                      }
                      style={{ marginBottom: 8 }}
                    >
                      {field.fieldType === "DT" ? (
                        <DatePicker style={{ width: "100%", borderRadius: 4 }} format="DD-MM-YYYY" />
                      ) : field.fieldType === "DTE" ? (
                        <DatePicker showTime style={{ width: "100%", borderRadius: 4 }} />
                      ) : field.fieldType === "NU" ? (
                        <Input type="number" style={{ borderRadius: 4 }} />
                      ) : field.fieldType === "LI" ? (
                        <Select placeholder="Select..." className="custom-select" style={{ width: "100%", borderRadius: 4 }}>
                          {field?.validationRule?.split(",").map((item) => (
                            <Select.Option key={item} value={item}>
                              {item}
                            </Select.Option>
                          ))}
                        </Select>
                      ) : (
                        <TextArea rows={2} style={{ borderRadius: 4 }} />
                      )}
                    </Form.Item>
                  ))}
                </Form>
              </div>
              <Button
                className="discount-apply-btn"
                style={{ width: "100%", marginTop: "0.8vh", height: "5vh", fontSize: "1em" }}
                onClick={() => {
                  try {
                    const values = discountAdditionalInfoForm.getFieldsValue();
                    const mandatoryFields = discountAdditionalInfo?.filter((f) => f.mandatory === "Y") || [];
                    const missing = mandatoryFields.filter((f) => !values[f.fieldName] && values[f.fieldName] !== 0);
                    if (missing.length > 0) {
                      message.warning(`Please fill: ${missing.map((f) => f.displayLabel).join(", ")}`);
                      return;
                    }
                    discountInforApplication(values);
                  } catch (err) {
                    console.error("[ManualDiscount] Submit error:", err);
                    message.error("Submit failed: " + err.message);
                  }
                }}
              >
                Submit
              </Button>
            </Col>
          )}
        </Row>
      </Form>
    </Drawer>
  );
};

export default ManualDiscountDrawer;
